import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from '../config/axiosClient';
import Config from '../config/Config.js';
import { RESOLVE_LOCAL_AUTH_STATE, RESOLVE_LOCAL_USER_STATE } from '../components/auth/AuthActions';
import SweetLoadingSpinner from '../components/eyeCandy/SweetLoadingSpinner.js';

const API_BASE = Config().url;
const REFRESH_INTERVAL = 30;

/* ===== DATE HELPERS ===== */

const formatRelativeDate = (dateStr) => {
    if (!dateStr) return dateStr;
    const today = new Date();
    const d = new Date(dateStr + 'T00:00:00');
    const diffDays = Math.round((today - d) / 86400000);
    const monthDay = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    if (diffDays === 0) return `${monthDay} (today)`;
    if (diffDays === 1) return `${monthDay} (yesterday)`;
    if (diffDays <= 7) return `${monthDay} (${diffDays}d ago)`;
    return monthDay;
};

const formatTimestamp = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now - d;
    const diffMin = Math.floor(diffMs / 60000);
    const diffHr = Math.floor(diffMin / 60);
    const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    const date = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    if (diffMin < 1) return 'just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    if (diffHr < 24) return `${time} (${diffHr}h ago)`;
    const diffDays = Math.floor(diffHr / 24);
    if (diffDays === 1) return `${time} (yesterday)`;
    if (diffDays <= 7) return `${date} ${time} (${diffDays}d ago)`;
    return `${date} ${time}`;
};

/* ===== MAIN PAGE ===== */

const TABS = ['overview', 'errors', 'traffic', 'platform', 'logs', 'tests', 'docs'];

const MetricsPage = () => {
    const auth = RESOLVE_LOCAL_AUTH_STATE();
    const [data, setData] = useState(null);
    const [days, setDays] = useState(7);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [tab, setTab] = useState('overview');
    const [newLogKeys, setNewLogKeys] = useState(new Set());
    const [countdown, setCountdown] = useState(REFRESH_INTERVAL);
    const intervalRef = useRef(null);
    const countdownRef = useRef(null);
    const prevLogKeysRef = useRef(new Set());

    const user = RESOLVE_LOCAL_USER_STATE();
    const isOwner = auth.is_authenticated === 'true' && user.pk === '1';

    const makeLogKey = (log) => `${log.timestamp}_${log.ip_address}_${log.path}_${log.method}`;

    const fetchData = useCallback(() => {
        return axios
            .get(`${API_BASE}api/v1/metrics/?days=${days}`, {
                headers: { Authorization: `Token ${auth.token}` },
            })
            .then((res) => {
                const newData = res.data;

                if (newData.recent_logs && prevLogKeysRef.current.size > 0) {
                    const fresh = new Set();
                    newData.recent_logs.forEach((log) => {
                        const key = makeLogKey(log);
                        if (!prevLogKeysRef.current.has(key)) fresh.add(key);
                    });
                    setNewLogKeys(fresh);
                    if (fresh.size > 0) {
                        setTimeout(() => setNewLogKeys(new Set()), 2000);
                    }
                }

                if (newData.recent_logs) {
                    const keys = new Set();
                    newData.recent_logs.forEach((log) => keys.add(makeLogKey(log)));
                    prevLogKeysRef.current = keys;
                }

                setData(newData);
                setError(null);
                setLoading(false);
                setCountdown(REFRESH_INTERVAL);
            })
            .catch((err) => {
                setError(err.message || 'Failed to load metrics.');
                setLoading(false);
            });
    }, [days, auth.token]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!isOwner) {
            setError('You don\u2019t have permission to view this page.');
            setLoading(false);
            return;
        }
        setLoading(true);
        setError(null);
        fetchData();
    }, [days, auth.token, isOwner, fetchData]);

    useEffect(() => {
        if (['overview', 'logs', 'errors'].includes(tab) && isOwner) {
            intervalRef.current = setInterval(() => { fetchData(); }, REFRESH_INTERVAL * 1000);
            countdownRef.current = setInterval(() => {
                setCountdown((c) => (c > 0 ? c - 1 : REFRESH_INTERVAL));
            }, 1000);
        }
        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
            if (countdownRef.current) clearInterval(countdownRef.current);
        };
    }, [tab, days, isOwner, fetchData]);

    if (error) {
        return (
            <div className="container" style={{ paddingTop: 50 }}>
                <p style={{ color: '#991b1b' }}>{error}</p>
            </div>
        );
    }

    return (
        <div className="container" style={{ paddingTop: 50, paddingBottom: 60 }}>
            <h4 style={{ marginBottom: 20, color: 'var(--ink)' }}>System</h4>

            <div style={{ display: 'flex', gap: 0, marginBottom: 24, borderBottom: '2px solid #eee', flexWrap: 'wrap' }}>
                {TABS.map((t) => (
                    <button
                        key={t}
                        onClick={() => setTab(t)}
                        style={{
                            padding: '10px 20px',
                            border: 'none',
                            borderBottom: tab === t ? '2px solid var(--brand, #59c6c1)' : '2px solid transparent',
                            marginBottom: -2,
                            background: 'none',
                            cursor: 'pointer',
                            fontFamily: 'Courier',
                            fontSize: '0.85rem',
                            fontWeight: tab === t ? 700 : 400,
                            color: tab === t ? 'var(--brand, #59c6c1)' : 'var(--ink, #888)',
                        }}
                    >
                        {t.charAt(0).toUpperCase() + t.slice(1)}
                    </button>
                ))}
            </div>

            {loading && <SweetLoadingSpinner loadingState={true} />}

            {data && tab === 'overview' && <OverviewTab data={data} countdown={countdown} days={days} setDays={setDays} />}
            {data && tab === 'errors' && <ErrorsTab data={data} countdown={countdown} days={days} setDays={setDays} />}
            {data && tab === 'traffic' && <TrafficTab data={data} days={days} setDays={setDays} />}
            {data && tab === 'platform' && <PlatformTab data={data} />}
            {data && tab === 'logs' && <LogsTab data={data} days={days} setDays={setDays} newLogKeys={newLogKeys} countdown={countdown} makeLogKey={makeLogKey} />}
            {tab === 'tests' && <TestsTab auth={auth} />}
            {tab === 'docs' && <DocsTab />}
        </div>
    );
};

/* ===== STATUS HELPERS ===== */

const STATUS_COLORS = {
    good: '#10b981',
    warn: '#f59e0b',
    bad: '#ef4444',
};

const getErrorRateStatus = (pct) => {
    if (pct < 1) return 'good';
    if (pct <= 5) return 'warn';
    return 'bad';
};

const getLatencyStatus = (ms) => {
    if (ms < 500) return 'good';
    if (ms <= 1000) return 'warn';
    return 'bad';
};

/* ===== OVERVIEW TAB ===== */

const ACTIVITY_LABELS = {
    signup: { label: 'Signup', color: '#10b981', icon: '+' },
    password_reset: { label: 'Password Reset', color: '#f59e0b', icon: '\u21BB' },
    project_create: { label: 'Project Created', color: '#3b82f6', icon: '\u25A0' },
    project_delete: { label: 'Project Deleted', color: '#ef4444', icon: '\u2212' },
    project_like: { label: 'Liked', color: '#ba3f9c', icon: '\u2665' },
};

const OverviewTab = ({ data, countdown, days, setDays }) => {
    const h = data.health || {};
    const er = h.error_rate || {};
    const lp = h.latency_percentiles || {};
    const ah = h.auth_health || {};
    const up = h.uptime_proxy || {};
    const activity = data.activity || [];
    const actSummary = data.activity_summary || {};

    const errorRateStatus = getErrorRateStatus(er.error_rate_pct || 0);
    const latencyStatus = getLatencyStatus(lp.p95 || 0);
    const authTotal = (ah.total_401 || 0) + (ah.total_403 || 0);
    const authStatus = authTotal > 100 ? 'bad' : authTotal > 20 ? 'warn' : 'good';

    return (
        <>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                <TimeSelector days={days} setDays={setDays} />
                <RefreshBadge countdown={countdown} />
            </div>

            {/* Health cards */}
            <div className="row" style={{ marginBottom: 30 }}>
                <HealthStatCard
                    label="Error Rate"
                    value={`${(er.error_rate_pct || 0).toFixed(1)}%`}
                    sub={`${er.total_5xx || 0} server / ${er.total_4xx || 0} client`}
                    status={errorRateStatus}
                />
                <HealthStatCard
                    label="Latency P95"
                    value={`${Math.round(lp.p95 || 0)}ms`}
                    sub={`P50: ${Math.round(lp.p50 || 0)}ms · P99: ${Math.round(lp.p99 || 0)}ms`}
                    status={latencyStatus}
                />
                <HealthStatCard
                    label="Auth Failures"
                    value={authTotal}
                    sub={`401: ${ah.total_401 || 0} / 403: ${ah.total_403 || 0}`}
                    status={authStatus}
                />
                <HealthStatCard
                    label="Uptime Checks"
                    value={up.healthcheck_count || 0}
                    sub={up.last_healthcheck ? `Last: ${formatTimestamp(up.last_healthcheck)}` : 'No checks'}
                    status="good"
                />
            </div>

            {/* Requests per day chart */}
            {data.requests && data.requests.per_day && data.requests.per_day.length > 0 && (
                <Section title="Requests Per Day">
                    <VerticalBarChart
                        items={data.requests.per_day.map((r) => ({
                            label: formatRelativeDate(r.date),
                            value: r.count,
                        }))}
                    />
                </Section>
            )}

            {/* Activity summary + feed */}
            <Section title="Activity">
                <div className="row" style={{ marginBottom: 20 }}>
                    <StatCard label="Signups" value={actSummary.signups || 0} />
                    <StatCard label="Password Resets" value={actSummary.password_resets || 0} />
                    <StatCard label="Projects Created" value={actSummary.project_creates || 0} />
                    <StatCard label="Likes" value={actSummary.project_likes || 0} />
                </div>

                {activity.length > 0 ? (
                    <div style={{ maxHeight: 400, overflowY: 'auto' }}>
                        {activity.map((evt, i) => {
                            const meta = ACTIVITY_LABELS[evt.type] || { label: evt.type, color: '#888', icon: '·' };
                            return (
                                <div key={i} style={{
                                    display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0',
                                    borderBottom: '1px solid #f3f3f3',
                                }}>
                                    <span style={{
                                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                        width: 24, height: 24, borderRadius: '50%', fontSize: '0.8rem',
                                        backgroundColor: meta.color + '18', color: meta.color, fontWeight: 700,
                                    }}>{meta.icon}</span>
                                    <span style={{
                                        display: 'inline-block', padding: '2px 8px', borderRadius: 4,
                                        fontSize: '0.7rem', fontFamily: 'Courier', fontWeight: 600,
                                        backgroundColor: meta.color + '18', color: meta.color,
                                    }}>{meta.label}</span>
                                    <span style={{ fontFamily: 'Courier', fontSize: '0.8rem', color: 'var(--ink, #333)' }}>
                                        {evt.username || 'anon'}
                                    </span>
                                    <span style={{ flex: 1 }} />
                                    <span style={{ fontFamily: 'Courier', fontSize: '0.75rem', color: '#999' }}>
                                        {formatTimestamp(evt.timestamp)}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <p style={{ color: '#888', fontFamily: 'Courier', fontSize: '0.85rem' }}>No activity in this period.</p>
                )}
            </Section>
        </>
    );
};

/* ===== ERRORS TAB ===== */

const STATUS_BADGE_COLORS = {
    5: { bg: '#fef2f2', color: '#991b1b', border: '#fecaca' },
    4: { bg: '#fffbeb', color: '#92400e', border: '#fde68a' },
};

const ErrorsTab = ({ data, countdown, days, setDays }) => {
    const [filter, setFilter] = useState('all');
    const [expandedRow, setExpandedRow] = useState(null);

    const logs = data.recent_logs || [];
    const errorLogs = logs.filter((l) => l.status_code >= 400);
    const logs5xx = errorLogs.filter((l) => l.status_code >= 500);
    const logs4xx = errorLogs.filter((l) => l.status_code >= 400 && l.status_code < 500);
    const filtered = filter === '5xx' ? logs5xx : filter === '4xx' ? logs4xx : errorLogs;

    // Error rate trend
    const h = data.health || {};
    const er = h.error_rate || {};

    // Group by path+status
    const grouped = {};
    errorLogs.forEach((l) => {
        const key = `${l.status_code} ${l.method} ${l.path}`;
        if (!grouped[key]) grouped[key] = { ...l, count: 0 };
        grouped[key].count++;
    });
    const topErrors = Object.values(grouped).sort((a, b) => b.count - a.count).slice(0, 10);

    return (
        <>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                <TimeSelector days={days} setDays={setDays} />
                <RefreshBadge countdown={countdown} />
            </div>

            {/* Summary cards */}
            <div className="row" style={{ marginBottom: 24 }}>
                <HealthStatCard
                    label="Error Rate"
                    value={`${(er.error_rate_pct || 0).toFixed(1)}%`}
                    status={getErrorRateStatus(er.error_rate_pct || 0)}
                />
                <HealthStatCard
                    label="5xx Errors"
                    value={er.total_5xx || 0}
                    status={(er.total_5xx || 0) > 0 ? 'bad' : 'good'}
                />
                <HealthStatCard
                    label="4xx Errors"
                    value={er.total_4xx || 0}
                    status={(er.total_4xx || 0) > 50 ? 'warn' : 'good'}
                />
                <HealthStatCard
                    label="Total Errors"
                    value={errorLogs.length}
                    sub="in recent logs"
                    status={errorLogs.length > 50 ? 'warn' : 'good'}
                />
            </div>

            {/* Error trend chart */}
            {er.errors_per_day && er.errors_per_day.length > 0 && (
                <Section title="5xx Errors Per Day">
                    <VerticalBarChart
                        items={er.errors_per_day.map((d) => ({
                            label: formatRelativeDate(d.date),
                            value: d.count,
                        }))}
                        barColor="#ef4444"
                    />
                </Section>
            )}

            {/* Top recurring errors */}
            {topErrors.length > 0 && (
                <Section title="Top Recurring Errors">
                    {topErrors.map((e, i) => {
                        const colors = STATUS_BADGE_COLORS[Math.floor(e.status_code / 100)] || STATUS_BADGE_COLORS[5];
                        return (
                            <div key={i} style={{
                                display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0',
                                borderBottom: '1px solid #f3f3f3',
                            }}>
                                <span style={{
                                    display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: '0.75rem',
                                    fontFamily: 'Courier', fontWeight: 700,
                                    backgroundColor: colors.bg, color: colors.color, border: `1px solid ${colors.border}`,
                                }}>{e.status_code}</span>
                                <span style={{ fontFamily: 'Courier', fontSize: '0.8rem', color: '#555', minWidth: 50 }}>{e.method}</span>
                                <span style={{ fontFamily: 'Courier', fontSize: '0.8rem', color: 'var(--ink, #333)', flex: 1 }}>{e.path}</span>
                                <span style={{ fontFamily: 'Courier', fontSize: '0.8rem', fontWeight: 700, color: colors.color }}>{e.count}x</span>
                            </div>
                        );
                    })}
                </Section>
            )}

            {/* Filter + detail table */}
            <Section title="Error Log">
                {errorLogs.length === 0 ? (
                    <p style={{ color: '#10b981', fontFamily: 'Courier', fontSize: '0.85rem' }}>No errors in recent logs.</p>
                ) : (
                    <>
                        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                            {[
                                { key: 'all', label: `All (${errorLogs.length})` },
                                { key: '5xx', label: `5xx (${logs5xx.length})` },
                                { key: '4xx', label: `4xx (${logs4xx.length})` },
                            ].map((f) => (
                                <button
                                    key={f.key}
                                    onClick={() => setFilter(f.key)}
                                    style={{
                                        padding: '4px 12px', border: '1px solid #ddd', borderRadius: 4,
                                        fontFamily: 'Courier', fontSize: '0.75rem', cursor: 'pointer',
                                        backgroundColor: filter === f.key ? (f.key === '5xx' ? '#fef2f2' : f.key === '4xx' ? '#fffbeb' : '#f0f9ff') : 'transparent',
                                        color: filter === f.key ? '#333' : '#888',
                                        fontWeight: filter === f.key ? 700 : 400,
                                    }}
                                >{f.label}</button>
                            ))}
                        </div>

                        <div style={{ maxHeight: 500, overflowY: 'auto' }}>
                            <table style={tableStyle}>
                                <thead>
                                    <tr>
                                        <th style={{ ...thStyle, width: 20 }}></th>
                                        <th style={thStyle}>Time</th>
                                        <th style={thStyle}>Status</th>
                                        <th style={thStyle}>Method</th>
                                        <th style={thStyle}>Path</th>
                                        <th style={thStyle}>User</th>
                                        <th style={thStyle}>IP</th>
                                        <th style={{ ...thStyle, textAlign: 'right' }}>ms</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filtered.map((log, i) => {
                                        const colors = STATUS_BADGE_COLORS[Math.floor(log.status_code / 100)] || STATUS_BADGE_COLORS[5];
                                        const hasDetail = log.error_detail && log.error_detail.length > 0;
                                        const isExpanded = expandedRow === i;
                                        return (
                                            <React.Fragment key={i}>
                                                <tr
                                                    onClick={() => hasDetail && setExpandedRow(isExpanded ? null : i)}
                                                    style={{ cursor: hasDetail ? 'pointer' : 'default' }}
                                                >
                                                    <td style={{ ...tdStyle, textAlign: 'center', fontSize: '0.7rem', color: '#999' }}>
                                                        {hasDetail ? (isExpanded ? '\u25BC' : '\u25B6') : ''}
                                                    </td>
                                                    <td style={{ ...tdStyle, whiteSpace: 'nowrap', fontSize: '0.8rem' }}>
                                                        {formatTimestamp(log.timestamp)}
                                                    </td>
                                                    <td style={tdStyle}>
                                                        <span style={{
                                                            display: 'inline-block', padding: '1px 6px', borderRadius: 3,
                                                            fontSize: '0.75rem', fontWeight: 700,
                                                            backgroundColor: colors.bg, color: colors.color,
                                                        }}>{log.status_code}</span>
                                                    </td>
                                                    <td style={tdStyle}>{log.method}</td>
                                                    <td style={tdStyle}>{log.path}</td>
                                                    <td style={tdStyle}>{log.user__username || 'anon'}</td>
                                                    <td style={tdStyle}>{log.ip_address || '-'}</td>
                                                    <td style={{ ...tdStyle, textAlign: 'right' }}>{log.response_time_ms ?? '-'}</td>
                                                </tr>
                                                {isExpanded && hasDetail && (
                                                    <tr>
                                                        <td colSpan={8} style={{ padding: 0 }}>
                                                            <pre style={{
                                                                margin: 0, padding: '12px 16px',
                                                                backgroundColor: '#1a1a2e', color: '#e0e0e0',
                                                                fontSize: '0.75rem', lineHeight: 1.5,
                                                                fontFamily: 'Courier', whiteSpace: 'pre-wrap',
                                                                wordBreak: 'break-word', maxHeight: 300,
                                                                overflowY: 'auto', borderBottom: '2px solid #ef4444',
                                                            }}>{log.error_detail}</pre>
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}
            </Section>
        </>
    );
};

/* ===== PLATFORM TAB ===== */

const PlatformTab = ({ data }) => (
    <>
        <div className="row" style={{ marginBottom: 30 }}>
            <StatCard label="Total Users" value={data.platform.total_users} />
            <StatCard label="Total Projects" value={data.platform.total_projects} />
            <StatCard label="Published" value={data.platform.published_projects} />
            <StatCard label="Unpublished" value={data.platform.unpublished_projects} />
        </div>

        <Section title="Projects By Owner (Top 5)">
            <SimpleBarChart
                items={data.platform.projects_by_owner.slice(0, 5).map((o) => ({
                    label: o.owner__username,
                    value: o.count,
                }))}
            />
        </Section>

        <Section title="Projects By Technology (Top 5)">
            <SimpleBarChart
                items={data.platform.projects_by_technology.slice(0, 5).map((t) => ({
                    label: t.technology,
                    value: t.count,
                }))}
            />
        </Section>

        <Section title="Projects By Skill (Top 5)">
            <SimpleBarChart
                items={data.platform.projects_by_skill.slice(0, 5).map((s) => ({
                    label: s.skill,
                    value: s.count,
                }))}
            />
        </Section>
    </>
);

/* ===== TRAFFIC TAB ===== */

const TrafficTab = ({ data, days, setDays }) => (
    <>
        <TimeSelector days={days} setDays={setDays} />

        <div className="row" style={{ marginBottom: 30 }}>
            <StatCard label={`Requests (${days}d)`} value={data.requests.total} />
            <StatCard label="Avg Response" value={data.requests.avg_response_ms !== null ? `${Math.round(data.requests.avg_response_ms)}ms` : 'N/A'} />
            <StatCard
                label="Auth / Anon"
                value={`${data.users.authenticated_vs_anonymous.authenticated} / ${data.users.authenticated_vs_anonymous.anonymous}`}
            />
            <StatCard label="Period" value={`${days} days`} />
        </div>

        <Section title="Requests Per Day">
            <VerticalBarChart
                items={data.requests.per_day.map((r) => ({
                    label: formatRelativeDate(r.date),
                    value: r.count,
                    sub: r.avg_response_ms !== null ? `${Math.round(r.avg_response_ms)}ms` : '',
                }))}
            />
        </Section>

        <Section title="Top Endpoints (Top 5)">
            <table style={tableStyle}>
                <thead>
                    <tr>
                        <th style={thStyle}>Path</th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>Hits</th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>Avg ms</th>
                    </tr>
                </thead>
                <tbody>
                    {data.requests.top_endpoints.slice(0, 5).map((e, i) => (
                        <tr key={i}>
                            <td style={tdStyle}>{e.path}</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{e.count}</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{e.avg_response_ms !== null ? Math.round(e.avg_response_ms) : '-'}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </Section>

        <div className="row">
            <div className="col-md-6">
                <Section title="Status Codes">
                    <SimpleBarChart
                        items={data.requests.status_codes.map((s) => ({
                            label: String(s.status_code),
                            value: s.count,
                        }))}
                    />
                </Section>
            </div>
            <div className="col-md-6">
                <Section title="By Method">
                    <SimpleBarChart
                        items={data.requests.by_method.map((m) => ({
                            label: m.method,
                            value: m.count,
                        }))}
                    />
                </Section>
            </div>
        </div>

        <Section title="Top Active Users (Top 5)">
            <table style={tableStyle}>
                <thead>
                    <tr>
                        <th style={thStyle}>ID</th>
                        <th style={thStyle}>User</th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>Requests</th>
                    </tr>
                </thead>
                <tbody>
                    {data.users.top_active.slice(0, 5).map((u, i) => (
                        <tr key={i}>
                            <td style={tdStyle}>{u.user__id}</td>
                            <td style={tdStyle}>{u.user__username}</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{u.count}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </Section>

        <Section title="Top IP Addresses (Top 5)">
            <table style={tableStyle}>
                <thead>
                    <tr>
                        <th style={thStyle}>IP Address</th>
                        <th style={thStyle}>User</th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>Requests</th>
                    </tr>
                </thead>
                <tbody>
                    {data.top_ips.slice(0, 5).map((ip, i) => (
                        <tr key={i}>
                            <td style={tdStyle}>{ip.ip_address || 'unknown'}</td>
                            <td style={tdStyle}>{ip.user__username || 'anon'}</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{ip.count}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </Section>
    </>
);

/* ===== LOGS TAB ===== */

const LogsTab = ({ data, days, setDays, newLogKeys, countdown, makeLogKey }) => {
    const [copied, setCopied] = useState(false);

    const copyAsCsv = () => {
        if (!data.recent_logs || data.recent_logs.length === 0) return;
        const headers = ['timestamp', 'ip_address', 'user', 'method', 'path', 'status_code', 'response_time_ms'];
        const escape = (v) => {
            const s = String(v ?? '');
            return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
        };
        const rows = data.recent_logs.map((log) => [
            log.timestamp,
            log.ip_address || '',
            log.user__username || 'anon',
            log.method,
            log.path,
            log.status_code,
            log.response_time_ms,
        ].map(escape).join(','));
        const csv = [headers.join(','), ...rows].join('\n');
        navigator.clipboard.writeText(csv).catch(() => {});
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
    <>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <TimeSelector days={days} setDays={setDays} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <button
                    onClick={copyAsCsv}
                    style={{
                        padding: '6px 14px',
                        border: '1px solid var(--border, #ddd)',
                        borderRadius: 4,
                        cursor: 'pointer',
                        fontFamily: 'Courier',
                        fontSize: '0.8rem',
                        backgroundColor: copied ? 'var(--brand, #59c6c1)' : 'transparent',
                        color: copied ? '#fff' : 'var(--ink, #333)',
                        transition: 'all 200ms ease',
                    }}
                >
                    {copied ? 'Copied!' : 'Copy as CSV'}
                </button>
                <RefreshBadge countdown={countdown} />
            </div>
        </div>

        {data.recent_logs && (
            <table style={tableStyle}>
                <thead>
                    <tr>
                        <th style={thStyle}>Time</th>
                        <th style={thStyle}>IP</th>
                        <th style={thStyle}>User</th>
                        <th style={thStyle}>Method</th>
                        <th style={thStyle}>Path</th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>Status</th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>ms</th>
                    </tr>
                </thead>
                <tbody>
                    {data.recent_logs.map((log, i) => {
                        const isNew = newLogKeys.has(makeLogKey(log));
                        return (
                            <tr key={i} style={{
                                backgroundColor: isNew ? '#59c6c1' : 'transparent',
                                transition: 'background-color 1.5s ease-out',
                            }}>
                                <td style={{ ...tdStyle, whiteSpace: 'nowrap', fontSize: '0.8rem' }}>
                                    {formatTimestamp(log.timestamp)}
                                </td>
                                <td style={tdStyle}>{log.ip_address || '-'}</td>
                                <td style={tdStyle}>{log.user__username || 'anon'}</td>
                                <td style={tdStyle}>{log.method}</td>
                                <td style={tdStyle}>{log.path}</td>
                                <td style={{ ...tdStyle, textAlign: 'right' }}>{log.status_code}</td>
                                <td style={{ ...tdStyle, textAlign: 'right' }}>{log.response_time_ms}</td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        )}
    </>
    );
};

/* ===== TESTS TAB ===== */

const TestsTab = ({ auth }) => {
    const [testData, setTestData] = useState(null);
    const [running, setRunning] = useState(false);
    const pollRef = useRef(null);

    const headers = { Authorization: `Token ${auth.token}` };

    const fetchStatus = useCallback(() => {
        return axios
            .get(`${API_BASE}api/v1/metrics/tests/`, { headers })
            .then((res) => {
                setTestData(res.data);
                if (res.data.status === 'running') {
                    setRunning(true);
                } else {
                    setRunning(false);
                    if (pollRef.current) {
                        clearInterval(pollRef.current);
                        pollRef.current = null;
                    }
                }
            })
            .catch(() => {});
    }, [auth.token]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        fetchStatus();
        return () => {
            if (pollRef.current) clearInterval(pollRef.current);
        };
    }, [fetchStatus]);

    const startTests = () => {
        setRunning(true);
        setTestData({ status: 'running', output: '', summary: { tests_run: 0, failures: 0, errors: 0 }, timestamp: null });
        axios
            .post(`${API_BASE}api/v1/metrics/tests/`, {}, { headers })
            .then(() => {
                pollRef.current = setInterval(fetchStatus, 2000);
            })
            .catch((err) => {
                if (err.response && err.response.status === 409) {
                    pollRef.current = setInterval(fetchStatus, 2000);
                } else {
                    setRunning(false);
                }
            });
    };

    const s = testData ? testData.summary || {} : {};
    const passed = (s.tests_run || 0) - (s.failures || 0) - (s.errors || 0);

    return (
        <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 }}>
                <button
                    onClick={startTests}
                    disabled={running}
                    style={{
                        padding: '10px 28px',
                        border: 'none',
                        borderRadius: 6,
                        cursor: running ? 'not-allowed' : 'pointer',
                        fontFamily: 'Courier',
                        fontSize: '0.95rem',
                        fontWeight: 700,
                        backgroundColor: running ? '#888' : 'var(--brand, #59c6c1)',
                        color: '#fff',
                        opacity: running ? 0.7 : 1,
                    }}
                >
                    {running ? 'Running...' : 'Run Tests'}
                </button>

                {running && (
                    <div style={{
                        display: 'inline-block',
                        width: 18, height: 18,
                        border: '3px solid #eee',
                        borderTop: '3px solid var(--brand, #59c6c1)',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite',
                    }} />
                )}

                {testData && testData.status !== 'idle' && testData.status !== 'running' && (
                    <span style={{
                        fontFamily: 'Courier', fontSize: '0.9rem', fontWeight: 700,
                        color: testData.status === 'passed' ? '#10b981' : '#ef4444',
                    }}>
                        {testData.status === 'passed' ? 'PASSED' : 'FAILED'}
                    </span>
                )}
            </div>

            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

            {testData && testData.status !== 'idle' && testData.status !== 'running' && (
                <div className="row" style={{ marginBottom: 24 }}>
                    <HealthStatCard label="Tests Run" value={s.tests_run || 0} status="good" />
                    <HealthStatCard label="Passed" value={passed} status="good" />
                    <HealthStatCard label="Failures" value={s.failures || 0} status={s.failures > 0 ? 'bad' : 'good'} />
                    <HealthStatCard label="Errors" value={s.errors || 0} status={s.errors > 0 ? 'bad' : 'good'} />
                </div>
            )}

            {testData && testData.timestamp && (
                <div style={{ fontFamily: 'Courier', fontSize: '0.8rem', color: '#999', marginBottom: 12 }}>
                    Last run: {formatTimestamp(testData.timestamp)}
                </div>
            )}

            {testData && testData.output && (
                <Section title="Test Output">
                    <pre style={{
                        backgroundColor: '#1e1e1e', color: '#d4d4d4',
                        padding: 20, borderRadius: 8, fontSize: '0.8rem',
                        fontFamily: 'Courier, monospace', overflowX: 'auto',
                        maxHeight: 600, overflowY: 'auto', lineHeight: 1.5,
                        whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                    }}>
                        {testData.output}
                    </pre>
                </Section>
            )}

            {(!testData || testData.status === 'idle') && !running && (
                <p style={{ color: '#888', fontFamily: 'Courier', marginTop: 20 }}>
                    No test results yet. Click "Run Tests" to execute the test suite.
                </p>
            )}
        </>
    );
};

/* ===== DOCS TAB ===== */

const docsBulletStyle = { fontFamily: 'Courier', fontSize: '0.85rem', color: 'var(--ink, #333)', lineHeight: 1.8, margin: 0, paddingLeft: 20 };
const docsSubBulletStyle = { ...docsBulletStyle, paddingLeft: 40, color: '#666' };
const docsLabelStyle = { color: 'var(--brand, #59c6c1)', fontWeight: 700 };

const DocsTab = () => (
    <>
        <Section title="Architecture Overview">
            <div style={{
                background: '#f6f8fa', border: '1px solid #e1e4e8', borderRadius: 8,
                padding: '20px 24px', fontFamily: 'Courier, monospace', fontSize: '0.85rem',
                lineHeight: 2, color: 'var(--ink, #333)', overflowX: 'auto', marginBottom: 20,
            }}>
                <div style={{ textAlign: 'center' }}>
                    <span style={docsLabelStyle}>React SPA</span> (S3 + CloudFront)<br/>
                    &darr;<br/>
                    <span style={docsLabelStyle}>ALB</span> (HTTPS, ACM cert)<br/>
                    &darr;<br/>
                    <span style={docsLabelStyle}>ECS Fargate</span> (Django API container)<br/>
                    &darr;<br/>
                    <span style={docsLabelStyle}>RDS PostgreSQL</span>
                </div>
            </div>
            <ul style={docsBulletStyle}>
                <li><span style={docsLabelStyle}>i7_client_react</span> -- React frontend, deployed to S3 + CloudFront</li>
                <li><span style={docsLabelStyle}>i7_api_django</span> -- Django REST API, deployed to ECS Fargate via CI/CD</li>
                <li><span style={docsLabelStyle}>i7_api_config</span> -- Terraform infrastructure configuration</li>
                <li><span style={docsLabelStyle}>Assets</span> -- S3 bucket (i7-assets-us-east-1) + CloudFront CDN, private with OAC</li>
            </ul>
        </Section>

        <Section title="API Endpoints">
            <div style={{ fontFamily: 'Courier', fontSize: '0.8rem', color: '#888', marginBottom: 12 }}>
                Base: /api/v1/
            </div>
            <table style={tableStyle}>
                <thead>
                    <tr>
                        <th style={thStyle}>Resource</th>
                        <th style={thStyle}>Endpoint</th>
                        <th style={thStyle}>Methods</th>
                        <th style={thStyle}>Notes</th>
                    </tr>
                </thead>
                <tbody>
                    <tr><td style={tdStyle} colSpan={4}><strong style={{ color: 'var(--brand, #59c6c1)' }}>Projects</strong></td></tr>
                    <tr><td style={tdStyle}>List / Create</td><td style={tdStyle}>/projects/</td><td style={tdStyle}>GET, POST</td><td style={tdStyle}>Public list, auth to create</td></tr>
                    <tr><td style={tdStyle}>Detail</td><td style={tdStyle}>/projects/&lt;id&gt;/</td><td style={tdStyle}>GET, PATCH, DELETE</td><td style={tdStyle}>View count incremented on GET</td></tr>
                    <tr><td style={tdStyle}>Like</td><td style={tdStyle}>/projects/&lt;id&gt;/like/</td><td style={tdStyle}>POST</td><td style={tdStyle}>Toggle like, requires auth</td></tr>
                    <tr><td style={tdStyle}>My Liked</td><td style={tdStyle}>/projects/liked/</td><td style={tdStyle}>GET</td><td style={tdStyle}>Current user's liked projects</td></tr>
                    <tr><td style={tdStyle}>User Liked</td><td style={tdStyle}>/projects/liked/&lt;user_pk&gt;/</td><td style={tdStyle}>GET</td><td style={tdStyle}>Public liked projects for user</td></tr>

                    <tr><td style={tdStyle} colSpan={4}><strong style={{ color: 'var(--brand, #59c6c1)' }}>Users</strong></td></tr>
                    <tr><td style={tdStyle}>List</td><td style={tdStyle}>/users/</td><td style={tdStyle}>GET</td><td style={tdStyle}>Basic user list</td></tr>
                    <tr><td style={tdStyle}>Detail</td><td style={tdStyle}>/users/&lt;id&gt;/</td><td style={tdStyle}>GET</td><td style={tdStyle}>Public profile</td></tr>
                    <tr><td style={tdStyle}>Extended List</td><td style={tdStyle}>/users/extended/</td><td style={tdStyle}>GET</td><td style={tdStyle}>With profile fields</td></tr>
                    <tr><td style={tdStyle}>Extended Detail</td><td style={tdStyle}>/users/extended/&lt;id&gt;/</td><td style={tdStyle}>GET, PATCH</td><td style={tdStyle}>Owner can edit</td></tr>

                    <tr><td style={tdStyle} colSpan={4}><strong style={{ color: 'var(--brand, #59c6c1)' }}>Auth</strong></td></tr>
                    <tr><td style={tdStyle}>Register</td><td style={tdStyle}>/rest-auth/registration/</td><td style={tdStyle}>POST</td><td style={tdStyle}>Email verification required</td></tr>
                    <tr><td style={tdStyle}>Login</td><td style={tdStyle}>/rest-auth/login/</td><td style={tdStyle}>POST</td><td style={tdStyle}>Returns token</td></tr>
                    <tr><td style={tdStyle}>Logout</td><td style={tdStyle}>/rest-auth/logout/</td><td style={tdStyle}>POST</td><td style={tdStyle}>Invalidates token</td></tr>
                    <tr><td style={tdStyle}>User</td><td style={tdStyle}>/rest-auth/user/</td><td style={tdStyle}>GET</td><td style={tdStyle}>Current user info</td></tr>
                    <tr><td style={tdStyle}>Password Reset</td><td style={tdStyle}>/rest-auth/password/reset/</td><td style={tdStyle}>POST</td><td style={tdStyle}>Sends reset email via SES</td></tr>
                    <tr><td style={tdStyle}>Password Change</td><td style={tdStyle}>/rest-auth/password/change/</td><td style={tdStyle}>POST</td><td style={tdStyle}>Requires auth</td></tr>
                    <tr><td style={tdStyle}>Email Confirm</td><td style={tdStyle}>/rest-auth/registration/verify-email/</td><td style={tdStyle}>POST</td><td style={tdStyle}>Verify email token</td></tr>

                    <tr><td style={tdStyle} colSpan={4}><strong style={{ color: 'var(--brand, #59c6c1)' }}>Tags</strong></td></tr>
                    <tr><td style={tdStyle}>List / Create</td><td style={tdStyle}>/tags/</td><td style={tdStyle}>GET, POST</td><td style={tdStyle}>POST is admin only</td></tr>
                    <tr><td style={tdStyle}>Detail</td><td style={tdStyle}>/tags/&lt;id&gt;/</td><td style={tdStyle}>GET, PATCH, DELETE</td><td style={tdStyle}>Admin only for writes</td></tr>

                    <tr><td style={tdStyle} colSpan={4}><strong style={{ color: 'var(--brand, #59c6c1)' }}>Uploads</strong></td></tr>
                    <tr><td style={tdStyle}>Presign</td><td style={tdStyle}>/uploads/presign/</td><td style={tdStyle}>POST</td><td style={tdStyle}>Returns presigned S3 URL</td></tr>

                    <tr><td style={tdStyle} colSpan={4}><strong style={{ color: 'var(--brand, #59c6c1)' }}>System</strong></td></tr>
                    <tr><td style={tdStyle}>Dashboard</td><td style={tdStyle}>/metrics/</td><td style={tdStyle}>GET</td><td style={tdStyle}>Admin only</td></tr>
                    <tr><td style={tdStyle}>Tests</td><td style={tdStyle}>/metrics/tests/</td><td style={tdStyle}>GET, POST</td><td style={tdStyle}>Admin only, POST triggers run</td></tr>
                </tbody>
            </table>
        </Section>

        <Section title="Authentication">
            <ul style={docsBulletStyle}>
                <li><span style={docsLabelStyle}>Token-based auth</span> -- ExpiringTokenAuthentication, tokens expire after 30 days</li>
                <li><span style={docsLabelStyle}>Email verification</span> -- Mandatory via django-allauth + Amazon SES</li>
                <li><span style={docsLabelStyle}>Username rules</span> -- Lowercase alphanumeric only, 3-20 characters, reserved names blocked</li>
                <li><span style={docsLabelStyle}>401 interceptor</span> -- Client auto-clears auth state and redirects to sign-in on 401</li>
                <li><span style={docsLabelStyle}>Token validation</span> -- VALIDATE_TOKEN() called on app load to verify token is still valid</li>
                <li><span style={docsLabelStyle}>Expired tokens</span> -- Automatically deleted on next use attempt</li>
            </ul>
        </Section>

        <Section title="Deployment">
            <ul style={docsBulletStyle}>
                <li><span style={docsLabelStyle}>Frontend</span> -- cd i7cr && bash deploy.sh</li>
                <ul style={docsSubBulletStyle}>
                    <li>yarn build &rarr; S3 sync &rarr; CloudFront invalidation</li>
                </ul>
                <li><span style={docsLabelStyle}>Backend</span> -- Push to master branch</li>
                <ul style={docsSubBulletStyle}>
                    <li>CI tests &rarr; Docker build &rarr; ECR push (i7_artifact:ci-&lt;sha&gt;) &rarr; GitHub release</li>
                    <li>Terraform apply &rarr; ECS service update</li>
                </ul>
                <li><span style={docsLabelStyle}>Infrastructure</span> -- Push to main in i7_api_config</li>
                <ul style={docsSubBulletStyle}>
                    <li>TFC workspace auto-triggers plan + apply</li>
                </ul>
            </ul>
        </Section>

        <Section title="Infrastructure">
            <ul style={docsBulletStyle}>
                <li><span style={docsLabelStyle}>Compute</span> -- ECS Fargate, 512 CPU units, 1024MB memory, single instance</li>
                <li><span style={docsLabelStyle}>Database</span> -- RDS PostgreSQL</li>
                <li><span style={docsLabelStyle}>Load Balancer</span> -- ALB with HTTPS (ACM certificate)</li>
                <li><span style={docsLabelStyle}>Frontend Hosting</span> -- S3 bucket (i7-client-react) + CloudFront CDN</li>
                <li><span style={docsLabelStyle}>Assets</span> -- S3 bucket (i7-assets-us-east-1) + CloudFront CDN, private with OAC</li>
                <li><span style={docsLabelStyle}>Secrets</span> -- AWS Secrets Manager (/ECS/i7)</li>
                <li><span style={docsLabelStyle}>Email</span> -- Amazon SES (us-east-1), SMTP via IAM user</li>
                <li><span style={docsLabelStyle}>Logs</span> -- CloudWatch for container logs</li>
                <li><span style={docsLabelStyle}>Container Runtime</span> -- Python 3.7 on Alpine Linux, Django 2.2.2, DRF 3.9.4</li>
            </ul>
        </Section>

        <Section title="Security">
            <ul style={docsBulletStyle}>
                <li><span style={docsLabelStyle}>CORS</span> -- Locked to inertia7.com domains only</li>
                <li><span style={docsLabelStyle}>IP blocking</span> -- Suspicious paths and known bad IPs are blocked</li>
                <li><span style={docsLabelStyle}>S3 assets</span> -- Private bucket, OAC access, presigned PUT URLs (5min expiry, 5MB max)</li>
                <li><span style={docsLabelStyle}>Slack alerts</span> -- Notifications for 500 errors and suspicious activity</li>
                <li><span style={docsLabelStyle}>Reserved usernames</span> -- Platform, system, and route names are blocked at registration</li>
            </ul>
        </Section>

        <Section title="Monitoring">
            <ul style={docsBulletStyle}>
                <li><span style={docsLabelStyle}>RequestLog</span> -- All requests logged with IP, user, path, method, status, response time, error detail</li>
                <li><span style={docsLabelStyle}>/system page</span> -- Overview, Errors, Traffic, Platform, Logs, Tests, Docs</li>
                <li><span style={docsLabelStyle}>Activity feed</span> -- Tracks signups, password resets, project creates/deletes</li>
                <li><span style={docsLabelStyle}>Slack alerts</span> -- Errors, new registrations, project CRUD operations</li>
                <li><span style={docsLabelStyle}>CloudWatch</span> -- Container logs streamed from ECS</li>
                <li><span style={docsLabelStyle}>Auto-refresh</span> -- Overview, Errors, and Logs tabs refresh every {REFRESH_INTERVAL}s</li>
            </ul>
        </Section>
    </>
);

/* ===== SHARED COMPONENTS ===== */

const RefreshBadge = ({ countdown }) => (
    <div style={{ fontFamily: 'Courier', fontSize: '0.8rem', color: '#999' }}>
        Refreshing in <span style={{ color: 'var(--brand, #59c6c1)', fontWeight: 700 }}>{countdown}s</span>
    </div>
);

const TimeSelector = ({ days, setDays }) => (
    <div style={{ display: 'flex', gap: 6 }}>
        {[7, 14, 30, 90].map((d) => (
            <button
                key={d}
                onClick={() => setDays(d)}
                style={{
                    padding: '6px 14px',
                    border: 'none',
                    borderRadius: 4,
                    cursor: 'pointer',
                    fontFamily: 'Courier',
                    fontSize: '0.85rem',
                    backgroundColor: days === d ? 'var(--brand, #59c6c1)' : 'var(--bg-soft, #f5f5f5)',
                    color: days === d ? '#fff' : 'var(--ink, #333)',
                }}
            >
                {d}d
            </button>
        ))}
    </div>
);

const HealthStatCard = ({ label, value, sub, status }) => {
    const color = STATUS_COLORS[status] || STATUS_COLORS.good;
    return (
        <div className="col-md-3 col-sm-6" style={{ marginBottom: 12 }}>
            <div
                style={{
                    background: 'var(--bg-soft, #f8f8f8)',
                    borderRadius: 8,
                    padding: '16px 20px',
                    textAlign: 'center',
                    borderLeft: `4px solid ${color}`,
                }}
            >
                <div style={{ fontSize: '1.6rem', fontWeight: 700, fontFamily: 'Courier', color: 'var(--ink, #222)' }}>{value}</div>
                <div style={{ fontSize: '0.8rem', color: '#888', marginTop: 4 }}>{label}</div>
                {sub && <div style={{ fontSize: '0.7rem', color: '#aaa', marginTop: 2 }}>{sub}</div>}
            </div>
        </div>
    );
};

const StatCard = ({ label, value }) => (
    <div className="col-md-3 col-sm-6" style={{ marginBottom: 12 }}>
        <div style={{ background: 'var(--bg-soft, #f8f8f8)', borderRadius: 8, padding: '16px 20px', textAlign: 'center' }}>
            <div style={{ fontSize: '1.6rem', fontWeight: 700, fontFamily: 'Courier', color: 'var(--ink, #222)' }}>{value}</div>
            <div style={{ fontSize: '0.8rem', color: '#888', marginTop: 4 }}>{label}</div>
        </div>
    </div>
);

const Section = ({ title, children }) => (
    <div style={{ marginBottom: 30 }}>
        <h6 style={{ color: 'var(--ink)', backgroundColor: 'var(--bg-soft)', padding: 5, display: 'inline-block' }}>{title}</h6>
        <hr style={{ marginTop: 4 }} />
        {children}
    </div>
);

/* Horizontal bars — good for categorical data (status codes, methods, users) */
const SimpleBarChart = ({ items, barColor }) => {
    if (!items || items.length === 0) return <p style={{ color: '#888' }}>No data</p>;
    const max = Math.max(...items.map((i) => i.value), 1);
    return (
        <div>
            {items.map((item, idx) => (
                <div key={idx} style={{ display: 'flex', alignItems: 'center', marginBottom: 6 }}>
                    <div style={{ width: 140, fontSize: '0.8rem', fontFamily: 'Courier', color: 'var(--ink, #333)', flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {item.label}
                    </div>
                    <div style={{ flex: 1, marginLeft: 8, marginRight: 8 }}>
                        <div style={{
                            height: 18,
                            width: `${(item.value / max) * 100}%`,
                            minWidth: 2,
                            backgroundColor: barColor || 'var(--brand, #59c6c1)',
                            borderRadius: 3,
                        }} />
                    </div>
                    <div style={{ fontSize: '0.8rem', fontFamily: 'Courier', fontWeight: 600, minWidth: 40, textAlign: 'right' }}>
                        {item.value}
                    </div>
                    {item.sub && <div style={{ fontSize: '0.7rem', color: '#999', marginLeft: 6, minWidth: 60 }}>{item.sub}</div>}
                </div>
            ))}
        </div>
    );
};

/* Vertical bars — time on x-axis */
const VerticalBarChart = ({ items, barColor }) => {
    if (!items || items.length === 0) return <p style={{ color: '#888' }}>No data</p>;
    const max = Math.max(...items.map((i) => i.value), 1);
    const barWidth = Math.max(16, Math.min(48, Math.floor(600 / items.length) - 8));
    return (
        <div style={{ overflowX: 'auto' }}>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, minHeight: 160, padding: '0 4px' }}>
                {items.map((item, idx) => {
                    return (
                        <div key={idx} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: items.length <= 14 ? 1 : 'none' }}>
                            {/* Value label */}
                            <div style={{ fontSize: '0.7rem', fontFamily: 'Courier', fontWeight: 600, color: 'var(--ink, #333)', marginBottom: 4 }}>
                                {item.value}
                            </div>
                            {/* Bar */}
                            <div style={{
                                width: barWidth,
                                height: Math.max(2, (item.value / max) * 120),
                                backgroundColor: barColor || 'var(--brand, #59c6c1)',
                                borderRadius: '3px 3px 0 0',
                            }} />
                            {/* Subtitle (e.g. avg ms) */}
                            {item.sub && (
                                <div style={{ fontSize: '0.6rem', color: '#999', marginTop: 2, fontFamily: 'Courier' }}>
                                    {item.sub}
                                </div>
                            )}
                            {/* Date label */}
                            <div style={{
                                fontSize: '0.65rem', fontFamily: 'Courier', color: '#888',
                                marginTop: 4, textAlign: 'center', whiteSpace: 'nowrap',
                                maxWidth: barWidth + 16, overflow: 'hidden', textOverflow: 'ellipsis',
                                lineHeight: 1.2,
                            }}>
                                {item.label}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

const tableStyle = { width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem', fontFamily: 'Courier' };
const thStyle = { borderBottom: '2px solid #ddd', padding: '6px 10px', textAlign: 'left', color: '#888', fontWeight: 600 };
const tdStyle = { borderBottom: '1px solid #eee', padding: '6px 10px', color: 'var(--ink, #333)' };

export default MetricsPage;
